import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

export default function PaymentResult({ status }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const { t } = useLang();
  const [state, setState] = useState(status === "cancel" ? "cancel" : "checking");
  const [result, setResult] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (status === "cancel") return;
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    let alive = true;
    const poll = async () => {
      if (!alive) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setResult(data);
          setState("success");
          await refresh();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setState("error"); return; }
      } catch {}
      if (attempts.current < 15) setTimeout(poll, 2000);
      else setState("error");
    };
    poll();
    return () => { alive = false; };
  }, [status, params, refresh]);

  const isSub = result?.type === "subscription";

  return (
    <div className="min-h-screen bg-[#0A0D14] flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl border border-[#243047] bg-[#121824] p-8 text-center fade-up" data-testid="payment-result-card">
        {state === "checking" && (<><Loader2 className="w-12 h-12 text-[#00FFC2] animate-spin mx-auto" /><h2 className="font-heading font-bold text-xl text-white mt-4">{t("pay.checking")}</h2></>)}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#00FFC2] mx-auto" />
            <h2 className="font-heading font-bold text-2xl text-white mt-4" data-testid="payment-success-title">
              {isSub ? t("pay.subOk", { plan: result.plan_name }) : t("pay.success")}
            </h2>
            <p className="text-[#94A3B8] mt-2" data-testid="payment-success-message">
              {isSub ? t("pay.subMsg", { n: result.daily_signals }) : t("pay.credits", { n: result?.signals || 0 })}
            </p>
          </>
        )}
        {state === "cancel" && (<><XCircle className="w-14 h-14 text-[#EC1E9C] mx-auto" /><h2 className="font-heading font-bold text-2xl text-white mt-4">{t("pay.cancel")}</h2><p className="text-[#94A3B8] mt-2">{t("pay.noCharge")}</p></>)}
        {state === "error" && (<><XCircle className="w-14 h-14 text-[#EC1E9C] mx-auto" /><h2 className="font-heading font-bold text-2xl text-white mt-4">{t("pay.error")}</h2><p className="text-[#94A3B8] mt-2">{t("pay.retry")}</p></>)}
        <button onClick={() => navigate("/dashboard")} data-testid="payment-back-dashboard"
          className="mt-6 w-full rounded-lg bg-gradient-to-r from-[#EC1E9C] to-[#00FFC2] text-[#03110B] font-bold py-3 hover:brightness-110 transition">
          {t("pay.back")}
        </button>
      </div>
    </div>
  );
}
